import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button, ConfirmDialog } from '@/components/ui';
import { useIsOrgAdmin } from '@/hooks/useIsOrgAdmin';
import { usePermissions } from '@/hooks/usePermissions';
import { useLoadSetupDemoData, useSetupWizardStatus } from '../hooks/useSetupWizard';

/**
 * Offers sample data to a workspace that has nobody in it yet. The API refuses
 * once real employees exist, so the banner goes away at the same point rather
 * than offering something that can only fail.
 */
export function DemoDataBanner(): React.ReactElement | null {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { can, isLoading: permissionsLoading } = usePermissions();
  const isOrgAdmin = useIsOrgAdmin();
  // The API allows only an organization admin, whatever the role's permissions.
  const canLoadDemoData =
    !permissionsLoading &&
    isOrgAdmin &&
    can('import_setup', 'read') &&
    can('import_setup', 'manage_demo_data');
  const { data: status } = useSetupWizardStatus(undefined, { enabled: canLoadDemoData });
  const loadDemoData = useLoadSetupDemoData();

  if (!canLoadDemoData || !status || !('organization' in status)) return null;

  // Production allows sample data only in demo organizations: never offer what
  // the server would refuse.
  if (!status.demoData?.allowedHere || status.demoData.loaded) return null;
  const alreadyLoaded = status.checklist.some(
    (item) => item.key === 'demo_data' && item.completed
  );
  if (alreadyLoaded || status.counts.employees > 1) return null;

  return (
    <section
      aria-label="Sample data"
      className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex min-w-0 items-start gap-3">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Explore with sample data
          </h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Add a demo company with three sample employees to see how things work before
            importing your team.
          </p>
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="w-full shrink-0 whitespace-nowrap sm:w-auto"
        onClick={() => setConfirmOpen(true)}
      >
        Add sample data
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() =>
          loadDemoData.mutate(false, { onSettled: () => setConfirmOpen(false) })
        }
        title="Add sample data?"
        message='This creates a company called "Demo Operations" with three sample employees. No emails are sent. You can remove them later from Users and Companies.'
        confirmLabel="Add sample data"
        confirmLoading={loadDemoData.isPending}
      />
    </section>
  );
}

DemoDataBanner.displayName = 'DemoDataBanner';
